import { useEffect, useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Icon } from "@/components/ui/Icon";
import { useAction, useItem } from "@/lib/api/queries";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import { dateTime, relative } from "@/lib/format";
import { ErrorState } from "@/components/ui/States";

/**
 * Serverdan tashqari zaxira (Telegram) sozlamasi.
 *
 * Nega panelda: administrator bot tokenini almashtirish yoki
 * guruhni ko'chirish uchun serverga SSH bilan kirmasligi kerak
 * (biznes qarori 07.10.2026). `.env` ni qo'lda tahrirlash har
 * safar ijrochini talab qilardi va o'zgarish hech qayerda qayd
 * etilmasdi.
 *
 * Maxfiy qiymat QAYTA KO'RSATILMAYDI: serverdan faqat niqob
 * keladi (`••••xyz9`). Bo'sh qoldirilsa eskisi saqlanadi —
 * shuning uchun «o'zgartirilmadi» va «tozalansin» ataylab
 * ajratilgan.
 */
interface Offsite {
  bot_token_mask: string;
  bot_token_set: boolean;
  api_hash_mask: string;
  api_hash_set: boolean;
  chat_id: string;
  api_id: string;
  key_recipient_id: string;
  configured: boolean;
  bot_api_url: string;
  key_available: boolean;
  encrypted: boolean;
  updated_at?: string;
  version: number;
  last_run?: {
    status: string;
    detail?: string;
    chunk_count: number;
    total_bytes: number;
    generation?: string;
    started_at?: string;
    finished_at: string;
  };
}

interface TestResult {
  chat_title?: string;
  can_pin: boolean;
  pin_error?: string;
}

export function Backup() {
  const session = useSession();
  const canManage = session.can(PERM.offsiteManage);
  const state = useItem<Offsite>("config", "/api/v1/offsite");

  // Maxfiy maydonlar: bo'sh — «o'zgartirilmadi». Panel niqobni
  // QAYTARMAYDI, aks holda har saqlashda token o'chib ketardi.
  const [botToken, setBotToken] = useState("");
  const [apiHash, setApiHash] = useState("");
  const [chatID, setChatID] = useState("");
  const [apiID, setApiID] = useState("");
  const [recipient, setRecipient] = useState("");
  const [test, setTest] = useState<TestResult | null>(null);
  const [keySent, setKeySent] = useState(false);

  // Ochiq qiymatlar serverdan kelganda formaga tushadi; maxfiy
  // maydonlar bo'sh qoladi.
  const loaded = state.data;
  useEffect(() => {
    if (!loaded) return;
    setChatID(loaded.chat_id);
    setApiID(loaded.api_id);
    setRecipient(loaded.key_recipient_id);
  }, [loaded]);

  const save = useAction<Offsite, void>("config", () => ({
    path: "/api/v1/offsite",
    method: "PUT",
    body: {
      // `null` — «tegilmadi»; bo'sh satr yuborilsa qiymat tozalanardi.
      bot_token: botToken.trim() === "" ? null : botToken.trim(),
      api_hash: apiHash.trim() === "" ? null : apiHash.trim(),
      chat_id: chatID.trim(),
      api_id: apiID.trim(),
      key_recipient_id: recipient.trim(),
    },
  }));
  const runTest = useAction<TestResult, void>("config", () => ({
    path: "/api/v1/offsite/test",
    method: "POST",
  }));
  const sendKey = useAction<{ sent: boolean }, void>("config", () => ({
    path: "/api/v1/offsite/send-key",
    method: "POST",
  }));

  const data = state.data;
  const run = data?.last_run;

  return (
    <>
      <PageHeader
        title="Serverdan tashqari zaxira"
        hint="Zaxira yopiq Telegram guruhiga 2 GB lik qismlar bo'lib ketadi. Shifrlash kaliti serverda qoladi — kalitsiz nusxa ochilmaydi."
      />

      {state.error && <ErrorState error={state.error} onRetry={() => void state.refetch()} />}

      {data && !data.encrypted && (
        <div className="notice">
          <Icon name="alert" size={16} />
          <span>
            Serverda shifrlash kaliti yo'q (<code>CONFIG_SECRET_KEY</code>) — sozlama
            saqlanmaydi. Token bazaga ochiq tushmasligi uchun ataylab shunday:
            kalitni <code>.env</code> ga qo'shib, config-service'ni qayta ishga tushiring.
          </span>
        </div>
      )}

      <section className="section">
        <div className="section-title">Bot va guruh</div>
        <div className="group">
          <div className="grid-2">
            <label className="field">
              <span className="field-label">Bot tokeni</span>
              <input
                className="input mono"
                type="password"
                autoComplete="off"
                placeholder={data?.bot_token_set ? data.bot_token_mask : "123456:ABC…"}
                value={botToken}
                onChange={(event) => setBotToken(event.target.value)}
              />
              <span className="field-hint">
                {data?.bot_token_set
                  ? "Kiritilgan. Bo'sh qoldirsangiz o'zgarmaydi."
                  : "Hali kiritilmagan. @BotFather bergan token."}
              </span>
            </label>
            <label className="field">
              <span className="field-label">Guruh IDsi (chat_id)</span>
              <input
                className="input mono"
                inputMode="numeric"
                placeholder="-1001234567890"
                value={chatID}
                onChange={(event) => setChatID(event.target.value)}
              />
              <span className="field-hint">
                Yopiq guruh IDsi manfiy son bo'ladi. Bot guruhda administrator
                bo'lishi SHART: katalog xabari qadaladi.
              </span>
            </label>
          </div>

          <div className="grid-2">
            <label className="field">
              <span className="field-label">api_id</span>
              <input
                className="input mono"
                inputMode="numeric"
                placeholder="2040"
                value={apiID}
                onChange={(event) => setApiID(event.target.value)}
              />
            </label>
            <label className="field">
              <span className="field-label">api_hash</span>
              <input
                className="input mono"
                type="password"
                autoComplete="off"
                placeholder={data?.api_hash_set ? data.api_hash_mask : "my.telegram.org dan"}
                value={apiHash}
                onChange={(event) => setApiHash(event.target.value)}
              />
            </label>
          </div>
          <p className="field-hint">
            <code>api_id</code> va <code>api_hash</code> — <code>my.telegram.org</code> dan.
            Ular o'z Bot API serverimiz uchun: bulut boti 50 MB dan katta fayl
            yubora olmaydi, zaxira qismlari esa 2 GB gacha.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="section-title">Zaxira kaliti</div>
        <div className="group">
          <label className="field">
            <span className="field-label">Kalit oluvchining Telegram IDsi</span>
            <input
              className="input mono"
              inputMode="numeric"
              placeholder="77001122"
              value={recipient}
              onChange={(event) => setRecipient(event.target.value)}
            />
            <span className="field-hint">
              Shaxsiy xabar keladi. Oluvchi avval botga <code>/start</code> yozgan
              bo'lishi kerak — aks holda Telegram xabarni qabul qilmaydi.
            </span>
          </label>
          <div className="notice">
            <Icon name="alert" size={16} />
            <span>
              Kalit (<code>WMS_OFFSITE_KEY</code>) bazada SAQLANMAYDI va bu yerda
              hech qachon ko'rinmaydi: u serverda qoladi va faqat yuqoridagi odamga
              yuboriladi. Kalit yo'qolsa zaxirani ochib bo'lmaydi — seyfga yozib qo'ying.
            </span>
          </div>
          {data && !data.key_available && (
            <p className="field-hint">
              Serverda <code>WMS_OFFSITE_KEY</code> sozlanmagan — yuborish ishlamaydi.
            </p>
          )}
        </div>
      </section>

      <section className="section">
        <div className="section-title">Amallar</div>
        <div className="group">
          <div className="dialog-actions" style={{ justifyContent: "flex-start", flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn btn-filled"
              disabled={!canManage || !data?.encrypted || save.isPending}
              onClick={() => {
                setTest(null);
                setKeySent(false);
                save.mutate(undefined, {
                  onSuccess: () => {
                    setBotToken("");
                    setApiHash("");
                    void state.refetch();
                  },
                });
              }}
            >
              <Icon name="check" size={16} />
              Saqlash
            </button>
            <button
              type="button"
              className="btn btn-tonal"
              disabled={!canManage || !data?.configured || runTest.isPending}
              onClick={() => {
                setKeySent(false);
                runTest.mutate(undefined, { onSuccess: (result) => setTest(result) });
              }}
            >
              <Icon name="refresh" size={16} />
              Sinab ko'rish
            </button>
            <button
              type="button"
              className="btn btn-tonal"
              disabled={
                !canManage || !data?.key_available || !data?.key_recipient_id || sendKey.isPending
              }
              onClick={() => {
                setTest(null);
                sendKey.mutate(undefined, { onSuccess: () => setKeySent(true) });
              }}
            >
              <Icon name="bell" size={16} />
              Kalitni qayta yuborish
            </button>
          </div>

          {!canManage && (
            <p className="field-hint">
              O'zgartirish uchun <code>offsite.manage</code> vakolati kerak.
            </p>
          )}
          {save.error && <ErrorState error={save.error} />}
          {runTest.error && <ErrorState error={runTest.error} />}
          {sendKey.error && <ErrorState error={sendKey.error} />}

          {/* Sinov natijasi: «yubordi» yetarli emas — qada olishi
              ham kerak, tiklash qadalgan katalogdan boshlanadi. */}
          {test && (
            <div className="notice">
              <Icon name={test.can_pin ? "check" : "alert"} size={16} />
              <span>
                Xabar yuborildi{test.chat_title ? ` — «${test.chat_title}»` : ""}.{" "}
                {test.can_pin
                  ? "Bot xabarni qadab ham ko'rdi: katalog joyiga tushadi."
                  : `Lekin QADAY OLMADI (${test.pin_error ?? "sabab noma'lum"}) — botni guruhda administrator qiling, aks holda tiklash katalogi topilmaydi.`}
              </span>
            </div>
          )}
          {keySent && (
            <div className="notice">
              <Icon name="check" size={16} />
              <span>Kalit yuborildi. Oluvchi uni seyfga yozib, xabarni o'chirsin.</span>
            </div>
          )}
        </div>
      </section>

      <section className="section">
        <div className="section-title">Oxirgi yuborish</div>
        <div className="group">
          {run ? (
            <dl className="kv">
              <dt>Holat</dt>
              <dd>
                <StatusBadge value={run.status} />
              </dd>
              <dt>Tugadi</dt>
              <dd title={dateTime(run.finished_at)}>{relative(run.finished_at)}</dd>
              <dt>Qismlar</dt>
              <dd className="num">{run.chunk_count}</dd>
              <dt>Hajmi</dt>
              <dd className="num">{gigabytes(run.total_bytes)}</dd>
              {run.generation && (
                <>
                  <dt>Avlod</dt>
                  <dd className="mono">{run.generation}</dd>
                </>
              )}
              {run.detail && (
                <>
                  <dt>Izoh</dt>
                  <dd>{run.detail}</dd>
                </>
              )}
            </dl>
          ) : (
            <p className="field-hint">
              Hali bitta ham yuborish qayd etilmagan. Zaxira jadval bo'yicha
              (<code>bin/offsite.py push</code>) ishlaydi va natijani shu yerga yozadi.
            </p>
          )}
          {data?.updated_at && (
            <p className="field-hint">
              Sozlama oxirgi marta {relative(data.updated_at)} o'zgargan (v{data.version}).
            </p>
          )}
        </div>
      </section>
    </>
  );
}

/** Bayt → GB: zaxira hajmi baytda o'qilmaydi. */
function gigabytes(bytes: number): string {
  if (!bytes) return "—";
  return `${(bytes / 1_000_000_000).toFixed(2)} GB`;
}
